import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import { usePaymentStatus } from '../hooks/usePaymentStatus';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../store/hooks';
import { selectSelectedProject, selectProjects } from '../../../Module/Project/Presentation/projectSelectors';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import DSLoading from '../../../components/designSystem/DSLoading';
import { useTranslation } from 'react-i18next';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import defaultProfileIcon from '../../../assets/Icon/project/default-profile.png';
import kakaoIcon from '../../../assets/Icon/project/kakao_icon.png';
import emailIcon from '../../../assets/Icon/project/email_icon.png';
import kakaoExampleImg from '../../../assets/img/share_modal/kakao_example.png';
import emailExampleImg from '../../../assets/img/share_modal/email_example.png';
import { openMailClient } from '../../../Utils/mail';

const PaymentStatusWeb = () => {
  const { t } = useTranslation('translation');
  const location = useLocation();
  const projects = useAppSelector(selectProjects);
  const selectedProject = useAppSelector(selectSelectedProject);
  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const minYear = 2025;
  const years = Array.from({ length: Math.max(currentYear - minYear + 1, 1) }, (_, index) => currentYear - index);
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const statuses = ['all', 'complete', 'fail'] as const;
  const latestAvailableYear = currentMonth === 1 ? currentYear - 1 : currentYear;
  const latestAvailableMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const selectableYears = years.filter((year) => year <= latestAvailableYear);

  const getMaxSelectableMonth = (year: number) => {
    if (year < latestAvailableYear) return 12;
    if (year === latestAvailableYear) return latestAvailableMonth;
    return 0;
  };

  const getMinSelectableMonth = (year: number) => {
    if (year === 2025) return 3;
    return 1;
  };

  const getDefaultYearMonth = () => {
    if (selectableYears.length > 0) {
      const defaultYear = selectableYears[0];
      return {
        year: defaultYear,
        month: getMaxSelectableMonth(defaultYear),
      };
    }

    return {
      year: years[0],
      month: 1,
    };
  };
  const defaultYearMonth = getDefaultYearMonth();

  const currentProject = useMemo(() => {
    if (projectId) {
      const found = projects.find((p) => p.project_id === projectId);
      if (found) return found;
      if (selectedProject?.project_id === projectId) return selectedProject;
    }
    return selectedProject || projects[0];
  }, [projectId, selectedProject, projects]);

  const projectTitle = currentProject?.title || t('common.projectTitlePlaceholder');

  const [activeTab, setActiveTab] = useState<'summary' | 'details'>('summary');
  const [selectedYear, setSelectedYear] = useState<number>(defaultYearMonth.year);
  const [selectedMonth, setSelectedMonth] = useState<number>(defaultYearMonth.month);
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'complete' | 'fail'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharePreviewType, setSharePreviewType] = useState<'kakao' | 'email' | null>(null);

  const yearSuffix = t('common.units.yearSuffix');
  const monthSuffix = t('common.units.monthSuffix');
  const monthLabel = (month: number) => `${month}${monthSuffix}`;
  const statusLabel = (status: typeof statuses[number]) => (status === 'all' ? t('common.filters.all') : t(`common.payment.status.${status}`));
  const selectableMonths = months.filter((month) => {
    return month >= getMinSelectableMonth(selectedYear) && month <= getMaxSelectableMonth(selectedYear);
  });

  // API 연동 훅 사용
  const {
    summary,
    details,
    hasData,
    isLoading,
    fetchSummary,
    fetchDetails
  } = usePaymentStatus(selectedYear, selectedMonth, currentProject?.project_id);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + t('common.units.won');
  };

  const effectiveProjectId = projectId ?? currentProject?.project_id;

  const runKakaoShare = () => {
    const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
    const shareTitle = projectTitle;
    const projectUrl = `http://go.MissionFund.org/${effectiveProjectId || ''}`;

    if (!(window as any).Kakao) {
      const script = document.createElement('script');
      script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      script.integrity = 'sha384-6MFdIr0zOira1CHQkedUqJVqe0ryQtcLs7ufRWw+T8rCXNKryCOgM/c7x5+z2JK7';
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        if (!(window as any).Kakao.isInitialized()) {
          (window as any).Kakao.init(KAKAO_KEY);
        }
        shareToKakao(shareTitle, projectUrl);
      };
      document.head.appendChild(script);
    } else {
      if (!(window as any).Kakao.isInitialized()) {
        (window as any).Kakao.init(KAKAO_KEY);
      }
      shareToKakao(shareTitle, projectUrl);
    }
  };

  const shareToKakao = (title: string, url: string) => {
    (window as any).Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: title,
        description: `${t('common.shareModal.descLine1')} ${t('common.shareModal.descLine2')}`,
        imageUrl: 'https://missionfund.org/static/media/main_logo.png',
        link: {
          mobileWebUrl: url,
          webUrl: url,
        },
      },
      buttons: [
        {
          title: t('common.shareModal.buttonDetail'),
          link: {
            mobileWebUrl: url,
            webUrl: url,
          },
        },
      ],
    });
  };

  const runEmailShare = () => {
    const shareTitle = projectTitle;
    const projectUrl = `http://go.MissionFund.org/${effectiveProjectId || ''}`;
    const subject = t('common.shareModal.emailSubject', { title: shareTitle });
    const body = t('common.shareModal.emailBody', { title: shareTitle, url: projectUrl });
    openMailClient({ subject, body });
  };

  const handleOpenKakaoPreview = () => {
    setIsShareModalOpen(false);
    setSharePreviewType('kakao');
  };

  const handleOpenEmailPreview = () => {
    setIsShareModalOpen(false);
    setSharePreviewType('email');
  };

  const handleCloseSharePreview = () => {
    setSharePreviewType(null);
  };

  const handleBackSharePreview = () => {
    setSharePreviewType(null);
    setIsShareModalOpen(true);
  };

  const handleConfirmSharePreview = () => {
    if (sharePreviewType === 'kakao') {
      runKakaoShare();
    } else if (sharePreviewType === 'email') {
      runEmailShare();
    }
    setSharePreviewType(null);
  };

  const handleCopyLink = async () => {
    const url = `http://go.MissionFund.org/${effectiveProjectId || ''}`;
    try {
      await navigator.clipboard.writeText(url);
      alert(t('common.messages.linkCopied'));
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(t('common.messages.linkCopied'));
    }
  };

  const handleExcelDownload = () => {
    if (!details || details.length === 0) return;

    // 필터링된 데이터 사용 (화면에 보이는 것과 동일하게)
    const filteredDetails = details.filter((item) => {
      const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
      const matchesSearch = item.name.includes(searchTerm);
      return matchesStatus && matchesSearch;
    });

    const headerStyle = {
      fill: { fgColor: { rgb: "D3D3D3" } },
      font: { bold: false, color: { rgb: "000000" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const cellStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const rightAlignStyle = {
      alignment: { horizontal: "right", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const descStyle = {
      alignment: { horizontal: "left", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const headers = [
      "No", "Sponsor name", "Donation Type", "Phone", "Payment Type",
      "Amount", "Date", "Status", "Description"
    ];

    const data = filteredDetails.map((item, idx) => ([
      filteredDetails.length - idx,
      item.name,
      item.donationType || "",
      item.phone,
      t(`common.payment.method.${item.method}`),
      `₩${new Intl.NumberFormat('ko-KR').format(item.amount)}`,
      item.date,
      t(`common.payment.status.${item.status}`),
      item.paymentDescription || ""
    ]));

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

    const wscols = [
      { wch: 5 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 80 }
    ];
    ws['!cols'] = wscols;

    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (ws[address]) ws[address].s = headerStyle;
      }
      for (let R = 1; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[address]) {
            ws[address].s = cellStyle;
            // Amount column (index 5) right alignment
            if (C === 5) {
              ws[address].s = rightAlignStyle;
            }
            // Description column (index 8) wrap text
            if (C === 8) {
              ws[address].s = descStyle;
            }
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payment Status");

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const projectIdForFile = String(currentProject?.project_id ?? effectiveProjectId ?? 'project')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_');
    const fileName = `onlove_payments_${projectIdForFile}_${yyyy}${mm}${dd}.xlsx`;

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
  };

  const handlePrevMonth = () => {
    if (selectedMonth === getMinSelectableMonth(selectedYear)) {
       const prevYear = selectedYear - 1;
       if (selectableYears.includes(prevYear)) {
         setSelectedYear(prevYear);
         setSelectedMonth(12);
       }
    } else {
       setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (isNextDisabled) return;

    if (selectedMonth === 12) {
       const nextYear = selectedYear + 1;
       if (selectableYears.includes(nextYear)) {
         setSelectedYear(nextYear);
         setSelectedMonth(getMinSelectableMonth(nextYear));
       }
    } else {
       setSelectedMonth(selectedMonth + 1);
    }
  };

  const isNextDisabled = useMemo(() => {
    if (selectedYear > latestAvailableYear) return true;
    if (selectedYear === latestAvailableYear && selectedMonth >= latestAvailableMonth) return true;
    return selectedMonth === 12 && !selectableYears.includes(selectedYear + 1);
  }, [selectedYear, selectedMonth, latestAvailableYear, latestAvailableMonth, selectableYears]);

  const renderSummary = () => (
    <>
      <SummaryCard>
        <SummaryTitleWrapper>
          <ArrowButton onClick={handlePrevMonth}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="22" viewBox="0 0 12 22" fill="none">
              <path d="M11.8899 1.76719L10.1216 0.000522614L0.489917 9.62885C0.33466 9.78313 0.211445 9.96659 0.127365 10.1687C0.0432855 10.3708 0 10.5875 0 10.8064C0 11.0252 0.0432855 11.2419 0.127365 11.444C0.211445 11.6461 0.33466 11.8296 0.489917 11.9839L10.1216 21.6172L11.8883 19.8505L2.84825 10.8089L11.8899 1.76719Z" fill="#17171B"/>
            </svg>
          </ArrowButton>
          <SummaryTitle>
            {selectedYear}{yearSuffix} {monthLabel(selectedMonth)} {t('page.paymentStatus.summary.titleSuffix')}
          </SummaryTitle>
          <ArrowButton onClick={handleNextMonth} disabled={isNextDisabled}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="22" viewBox="0 0 12 22" fill="none">
              <path d="M-0.000268936 1.76719L1.76806 0.000522614L11.3997 9.62885C11.555 9.78313 11.6782 9.96659 11.7623 10.1687C11.8464 10.3708 11.8896 10.5875 11.8896 10.8064C11.8896 11.0252 11.8464 11.2419 11.7623 11.444C11.6782 11.6461 11.555 11.8296 11.3997 11.9839L1.76806 21.6172L0.00139713 19.8505L9.0414 10.8089L-0.000268936 1.76719Z" fill={isNextDisabled ? "#BCBCBC" : "#17171B"}/>
            </svg>
          </ArrowButton>
        </SummaryTitleWrapper>
        {isLoading ? (
          <DSLoading />
        ) : (
          <SummaryContent>
            <SummaryItem variant="yellow">
              <ItemLabelGroup>
                <ItemLabel>{t('page.paymentStatus.summary.labels.promised')}</ItemLabel>
                <ItemValue>{formatCurrency(summary.promisedAmount)}</ItemValue>
              </ItemLabelGroup>
              <ItemDescription>{t('page.paymentStatus.summary.descriptions.promised')}</ItemDescription>
            </SummaryItem>

            <SummaryItem variant="white">
              <ItemLabelGroup>
                <ItemLabel size="small">{t('page.paymentStatus.summary.labels.failed')}</ItemLabel>
                <ItemValue negative={true}>{formatCurrency(summary.failedAmount)}</ItemValue>
              </ItemLabelGroup>
              <ItemDescription>
                {t('page.paymentStatus.summary.descriptions.failedLine1')} {t('page.paymentStatus.summary.descriptions.failedLine2')}
              </ItemDescription>
            </SummaryItem>

            <SummaryItem variant="blue">
              <ItemLabelGroup>
                <ItemLabel>{t('page.paymentStatus.summary.labels.completed')}</ItemLabel>
                <ItemValue highlight={true}>{formatCurrency(summary.completedAmount)}</ItemValue>
              </ItemLabelGroup>
              <ItemDescription>{t('page.paymentStatus.summary.descriptions.completed')}</ItemDescription>
            </SummaryItem>

            <SummaryItem variant="white">
              <ItemLabelGroup>
                <ItemLabel size="small">{t('page.paymentStatus.summary.labels.fees')}</ItemLabel>
                <ItemValue negative={true}>{formatCurrency(summary.fees)}</ItemValue>
              </ItemLabelGroup>
              <ItemDescription>{t('page.paymentStatus.summary.descriptions.fees')}</ItemDescription>
            </SummaryItem>

            <SummaryItem variant="white">
              <ItemLabelGroup>
                <ItemLabel size="small">{t('page.paymentStatus.summary.labels.missionFund')}</ItemLabel>
                <ItemValue negative={true}>{formatCurrency(summary.missionFund)}</ItemValue>
              </ItemLabelGroup>
              <ItemDescription>{t('page.paymentStatus.summary.descriptions.missionFund')}</ItemDescription>
            </SummaryItem>

            <FinalDepositBox>
              <ItemLabelGroup>
                <FinalLabel>{t('page.paymentStatus.summary.labels.finalDeposit')}</FinalLabel>
                <FinalValue>{formatCurrency(summary.finalDeposit)}</FinalValue>
              </ItemLabelGroup>
              <ItemDescription>{t('page.paymentStatus.summary.descriptions.finalDeposit')}</ItemDescription>
            </FinalDepositBox>
          </SummaryContent>
        )}
      </SummaryCard>

      <NoticeCard>
        <NoticeTitle>{t('page.paymentStatus.notice.title')}</NoticeTitle>
        <NoticeList>
          <NoticeItem>{t('page.paymentStatus.notice.items.first')}</NoticeItem>
          <NoticeItem>{t('page.paymentStatus.notice.items.second')}</NoticeItem>
          <NoticeItem>{t('page.paymentStatus.notice.items.third')}</NoticeItem>
        </NoticeList>
      </NoticeCard>
    </>
  );

  const renderDetails = () => {
    const filteredDetails = details.filter((item) => {
      const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
      const matchesSearch = item.name.includes(searchTerm);
      return matchesStatus && matchesSearch;
    });

    return (
      <>
        <GlobalFilterSection>
          <FilterGroup>
            <SearchInputWrapper>
              <SearchInput
                placeholder={t('page.paymentStatus.filters.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <SearchIcon />
            </SearchInputWrapper>
            <SelectWrapper isOpen={isStatusDropdownOpen} onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}>
              <SelectedText>{selectedStatus === 'all' ? t('page.paymentStatus.filters.statusSelect') : statusLabel(selectedStatus)}</SelectedText>
              <ArrowIcon isOpen={isStatusDropdownOpen} />
              {isStatusDropdownOpen && (
                <DropdownList>
                  {statuses.map((status) => (
                    <DropdownItem
                      key={status}
                      isActive={selectedStatus === status}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStatus(status);
                        setIsStatusDropdownOpen(false);
                      }}
                    >
                      {statusLabel(status)}
                    </DropdownItem>
                  ))}
                </DropdownList>
              )}
            </SelectWrapper>
          </FilterGroup>
        </GlobalFilterSection>

        <DetailList>
          {filteredDetails.length > 0 ? (
            filteredDetails.map((item, index) => (
              <DetailItem key={item.id} isFirst={index === 0} isLast={index === filteredDetails.length - 1}>
                <ProfileImg 
                  src={item.profileImg || defaultProfileIcon} 
                  alt={item.name} 
                  onError={(e) => { e.currentTarget.src = defaultProfileIcon; }}
                />
                <ItemContent>
                  <ItemHeader>
                    <DonorName>{item.name}</DonorName>
                    <BadgeGroup>
                      <Badge type={item.method}>{t(`common.payment.method.${item.method}`)}</Badge>
                      <Badge type={item.status}>{t(`common.payment.status.${item.status}`)}</Badge>
                    </BadgeGroup>
                  </ItemHeader>
                  <DonorInfoGroup>
                    <InfoItem>
                      <EmailIcon />
                      {item.email}
                    </InfoItem>
                    <InfoItem>
                      <PhoneIcon />
                      {item.phone}
                    </InfoItem>
                    <InfoItem>
                      <DateIcon />
                      {item.date} {t('common.labels.registered')}
                    </InfoItem>
                  </DonorInfoGroup>
                  {item.message ? <DonorMessage>{item.message}</DonorMessage> : null}
                  <AmountSection>
                    <AmountBox>
                      <AmountLabel>{t('page.paymentStatus.details.amountLabel')}</AmountLabel>
                      <AmountValue color="main">{formatCurrency(item.amount)}</AmountValue>
                    </AmountBox>
                    <AmountBox>
                      <AmountLabel>{t('page.paymentStatus.details.feeLabel')}</AmountLabel>
                      <AmountValue color="sub">{formatCurrency(item.fee)}</AmountValue>
                    </AmountBox>
                  </AmountSection>
                </ItemContent>
              </DetailItem>
            ))
          ) : (
            <NoResults>
              <NoResultsText>{t('page.paymentStatus.details.noResults')}</NoResultsText>
              <NoResultsIcon />
            </NoResults>
          )}
        </DetailList>
      </>
    );
  };

  return (
    <WebPageLayout title={t('common.layout.projectManagement')} sidebarActive="payments">
      <Container>
        <ProjectDetailHeader title={projectTitle} projectId={currentProject?.project_id} variant="web" />
        <PageTitleSection>
          <PageTitle>{t('page.paymentStatus.pageTitle')}</PageTitle>
        </PageTitleSection>

        {isLoading ? (
          <LoadingWrapper>
            <DSLoading />
          </LoadingWrapper>
        ) : !hasData ? (
          <>
            <GlobalFilterSection>
              <FilterGroup>
                <SelectWrapper isOpen={isYearDropdownOpen} onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}>
                  <SelectedText>{selectedYear}{yearSuffix}</SelectedText>
                  <ArrowIcon isOpen={isYearDropdownOpen} />
                  {isYearDropdownOpen && (
                    <DropdownList>
                      {selectableYears.map((year) => (
                        <DropdownItem
                          key={year}
                          isActive={selectedYear === year}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedYear(year);
                            const minM = getMinSelectableMonth(year);
                            const maxM = getMaxSelectableMonth(year);
                            setSelectedMonth((prevMonth) => Math.max(minM, Math.min(prevMonth, maxM)));
                            setIsYearDropdownOpen(false);
                          }}
                        >
                          {year}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  )}
                </SelectWrapper>
                <SelectWrapper isOpen={isMonthDropdownOpen} onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}>
                  <SelectedText>{`${selectedMonth}${monthSuffix}`}</SelectedText>
                  <ArrowIcon isOpen={isMonthDropdownOpen} />
                  {isMonthDropdownOpen && (
                    <DropdownList>
                      {selectableMonths.map((month) => (
                        <DropdownItem
                          key={month}
                          isActive={selectedMonth === month}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMonth(month);
                            setIsMonthDropdownOpen(false);
                          }}
                        >
                          {month}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  )}
                </SelectWrapper>
              </FilterGroup>
              <ExcelButton onClick={handleExcelDownload}>
                <DownloadIcon />
                {t('common.actions.downloadExcel')}
              </ExcelButton>
            </GlobalFilterSection>

            <TabWrapper>
              <Tab active={activeTab === 'summary'} onClick={() => setActiveTab('summary')}>
                <TabInner>
                  <SummaryTabIcon active={activeTab === 'summary'} />
                  {t('page.paymentStatus.tabs.summary')}
                </TabInner>
              </Tab>
              <Tab active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
                <TabInner>
                  <DetailsTabIcon active={activeTab === 'details'} />
                  {t('page.paymentStatus.tabs.details')}
                </TabInner>
              </Tab>
            </TabWrapper>

            <EmptyStateWrapper>
              <EmptyClockIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 160" fill="none">
                <path d="M80 140C71.6667 140 63.8622 138.418 56.5867 135.253C49.3111 132.089 42.9778 127.811 37.5867 122.42C32.1956 117.029 27.9178 110.696 24.7533 103.42C21.5889 96.1444 20.0045 88.3378 20 80C19.9956 71.6622 21.58 63.8578 24.7533 56.5867C27.9267 49.3156 32.2022 42.9822 37.58 37.5867C42.9578 32.1911 49.2911 27.9133 56.58 24.7533C63.8689 21.5933 71.6756 20.0089 80 20C89.1111 20 97.7511 21.9444 105.92 25.8333C114.089 29.7222 121.004 35.2222 126.667 42.3333V33.3333C126.667 31.4444 127.307 29.8622 128.587 28.5867C129.867 27.3111 131.449 26.6711 133.333 26.6667C135.218 26.6622 136.802 27.3022 138.087 28.5867C139.371 29.8711 140.009 31.4533 140 33.3333V60C140 61.8889 139.36 63.4733 138.08 64.7533C136.8 66.0333 135.218 66.6711 133.333 66.6667H106.667C104.778 66.6667 103.196 66.0267 101.92 64.7467C100.644 63.4667 100.004 61.8844 100 60C99.9956 58.1156 100.636 56.5333 101.92 55.2533C103.204 53.9733 104.787 53.3333 106.667 53.3333H118.333C113.778 47.1111 108.167 42.2222 101.5 38.6667C94.8333 35.1111 87.6667 33.3333 80 33.3333C67 33.3333 55.9733 37.8622 46.92 46.92C37.8667 55.9778 33.3378 67.0044 33.3333 80C33.3289 92.9956 37.8578 104.024 46.92 113.087C55.9822 122.149 67.0089 126.676 80 126.667C90.5556 126.667 100 123.5 108.333 117.167C116.667 110.833 122.167 102.667 124.833 92.6667C125.389 90.8889 126.389 89.5556 127.833 88.6667C129.278 87.7778 130.889 87.4444 132.667 87.6667C134.556 87.8889 136.056 88.6933 137.167 90.08C138.278 91.4667 138.611 92.9956 138.167 94.6667C134.944 107.889 127.944 118.751 117.167 127.253C106.389 135.756 94 140.004 80 140ZM86.6667 77.3333L103.333 94C104.556 95.2222 105.167 96.7778 105.167 98.6667C105.167 100.556 104.556 102.111 103.333 103.333C102.111 104.556 100.556 105.167 98.6667 105.167C96.7778 105.167 95.2222 104.556 94 103.333L75.3333 84.6667C74.6667 84 74.1667 83.2511 73.8333 82.42C73.5 81.5889 73.3333 80.7267 73.3333 79.8333V53.3333C73.3333 51.4444 73.9733 49.8622 75.2533 48.5867C76.5333 47.3111 78.1156 46.6711 80 46.6667C81.8844 46.6622 83.4689 47.3022 84.7533 48.5867C86.0378 49.8711 86.6756 51.4533 86.6667 53.3333V77.3333Z" fill="#DEF9FF"/>
              </EmptyClockIcon>
              <EmptyTitleBlue>데이터를 준비 중입니다</EmptyTitleBlue>
              <EmptyTitleBlack>
                현재 정산 시스템 점검 또는 집계 지연으로
                <br />
                해당 월의 결제 현황을 표시할 수 없습니다.
              </EmptyTitleBlack>
            </EmptyStateWrapper>

            <NoticeCard>
              <NoticeTitle>{t('page.paymentStatus.notice.title')}</NoticeTitle>
              <NoticeList>
                <NoticeItem>{t('page.paymentStatus.notice.items.first')}</NoticeItem>
                <NoticeItem>{t('page.paymentStatus.notice.items.second')}</NoticeItem>
                <NoticeItem>{t('page.paymentStatus.notice.items.third')}</NoticeItem>
              </NoticeList>
            </NoticeCard>

            {isShareModalOpen && (
              <ModalOverlay onClick={() => setIsShareModalOpen(false)}>
                <ModalContainer onClick={(e) => e.stopPropagation()}>
                  <ModalHeader>
                    <ModalTitleGroup>
                      <ModalTitle>{t('page.donorManagement.shareModal.title')}</ModalTitle>
                      <ModalDesc>{t('common.shareModal.descLine1')} {t('common.shareModal.descLine2')}</ModalDesc>
                    </ModalTitleGroup>
                    <CloseIcon onClick={() => setIsShareModalOpen(false)} />
                  </ModalHeader>
                  <ModalBody>
                    <ShareOption onClick={handleOpenKakaoPreview}>
                      <KakaoIconBox>
                        <KakaoIconImg src={kakaoIcon} alt={t('common.shareModal.kakaoAlt')} />
                      </KakaoIconBox>
                      <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
                    </ShareOption>
                    <ShareOption onClick={handleOpenEmailPreview}>
                      <EmailIconBox>
                        <EmailIconLarge src={emailIcon} alt={t('common.shareModal.emailAlt')} />
                      </EmailIconBox>
                      <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
                    </ShareOption>
                  </ModalBody>
                  <ModalFooter>
                    <LinkBox>
                      <LinkLabel>{t('common.shareModal.linkLabel')}</LinkLabel>
                      <LinkUrl>{`http://go.MissionFund.org/${effectiveProjectId || ''}`}</LinkUrl>
                    </LinkBox>
                    <CopyButton onClick={handleCopyLink}>
                      <CopyIcon />
                      {t('common.shareModal.copyLink')}
                    </CopyButton>
                  </ModalFooter>
                </ModalContainer>
              </ModalOverlay>
            )}
            {sharePreviewType && (
              <SharePreviewOverlay onClick={handleCloseSharePreview}>
                <SharePreviewContainer onClick={(e) => e.stopPropagation()}>
                  <SharePreviewHeader>
                    <SharePreviewTitleRow>
                      <SharePreviewTitle>
                        {sharePreviewType === 'kakao' ? t('common.shareModal.kakaoOption') : t('common.shareModal.emailOption')}
                      </SharePreviewTitle>
                      <SharePreviewCloseButton onClick={handleCloseSharePreview} aria-label="close" />
                    </SharePreviewTitleRow>
                    <SharePreviewDesc>
                      {sharePreviewType === 'kakao' ? '카톡으로 새 모금 프로젝트를 전합니다.' : '이메일로 새소식을 전합니다.'}
                    </SharePreviewDesc>
                  </SharePreviewHeader>
                  <SharePreviewBody>
                    <SharePreviewImage
                      src={sharePreviewType === 'kakao' ? kakaoExampleImg : emailExampleImg}
                      alt={sharePreviewType === 'kakao' ? t('common.shareModal.kakaoAlt') : t('common.shareModal.emailAlt')}
                    />
                  </SharePreviewBody>
                  <SharePreviewFooter>
                    <SharePreviewBackButton onClick={handleBackSharePreview}>
                      {t('common.actions.back')}
                    </SharePreviewBackButton>
                    <SharePreviewSendButton onClick={handleConfirmSharePreview}>
                      보내기
                    </SharePreviewSendButton>
                  </SharePreviewFooter>
                </SharePreviewContainer>
              </SharePreviewOverlay>
            )}
          </>
        ) : (
          <>
            <GlobalFilterSection>
            <FilterGroup>
              <SelectWrapper isOpen={isYearDropdownOpen} onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}>
                <SelectedText>{selectedYear}{yearSuffix}</SelectedText>
                <ArrowIcon isOpen={isYearDropdownOpen} />
                {isYearDropdownOpen && (
                  <DropdownList>
                    {selectableYears.map((year) => (
                      <DropdownItem
                        key={year}
                        isActive={selectedYear === year}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedYear(year);
                          const minM = getMinSelectableMonth(year);
                          const maxM = getMaxSelectableMonth(year);
                          setSelectedMonth((prevMonth) => Math.max(minM, Math.min(prevMonth, maxM)));
                          setIsYearDropdownOpen(false);
                        }}
                      >
                        {year}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </SelectWrapper>
              <SelectWrapper isOpen={isMonthDropdownOpen} onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}>
                <SelectedText>{`${selectedMonth}${monthSuffix}`}</SelectedText>
                <ArrowIcon isOpen={isMonthDropdownOpen} />
                {isMonthDropdownOpen && (
                  <DropdownList>
                    {selectableMonths.map((month) => (
                      <DropdownItem
                        key={month}
                        isActive={selectedMonth === month}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMonth(month);
                          setIsMonthDropdownOpen(false);
                        }}
                      >
                        {monthLabel(month)}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </SelectWrapper>
            </FilterGroup>
            <ExcelButton onClick={handleExcelDownload}>
              <DownloadIcon />
              {t('common.actions.downloadExcel')}
            </ExcelButton>
          </GlobalFilterSection>

          <TabWrapper>
            <Tab active={activeTab === 'summary'} onClick={() => setActiveTab('summary')}>
              <TabInner>
                <SummaryTabIcon active={activeTab === 'summary'} />
                {t('page.paymentStatus.tabs.summary')}
              </TabInner>
            </Tab>
            <Tab active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
              <TabInner>
                <DetailsTabIcon active={activeTab === 'details'} />
                {t('page.paymentStatus.tabs.details')}
              </TabInner>
            </Tab>
          </TabWrapper>

          {activeTab === 'summary' ? renderSummary() : renderDetails()}
        </>
        )}
      </Container>
    </WebPageLayout>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding-right: 0;
  margin-bottom: 40px;
`;

const PageTitleSection = styled.div`
  margin-bottom: 12px;
`;

const PageTitle = styled.h1`
  ${({ theme }) => theme.typography.head.semiBold24}
  color: #000000;
  margin: 0;
`;

const TabWrapper = styled.div`
  display: flex;
  gap: 48px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  padding: 0 32px;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border-left: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-right: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-top: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  margin-bottom: 12px;
`;

const Tab = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  padding: 8px 0;
  padding-top: 12px;
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme, active }) => (active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[50])};
  border-bottom: 4px solid ${({ theme, active }) => (active ? theme.colors.primaryBlue[40] : 'transparent')};
  cursor: pointer;
  margin-bottom: -1px;
`;

const TabInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SummaryTabIcon = ({ active }: { active: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden focusable="false">
    <path fillRule="evenodd" clipRule="evenodd" d="M15.7491 8.99911C15.7491 9.88554 15.5745 10.7633 15.2353 11.5822C14.8961 12.4012 14.3989 13.1453 13.7721 13.7721C13.1453 14.3989 12.4012 14.8961 11.5822 15.2353C10.7633 15.5745 9.88554 15.7491 8.99911 15.7491C8.11269 15.7491 7.23495 15.5745 6.416 15.2353C5.59705 14.8961 4.85294 14.3989 4.22614 13.7721C3.59935 13.1453 3.10215 12.4012 2.76293 11.5822C2.42371 10.7633 2.24911 9.88554 2.24911 8.99911C2.24911 7.2089 2.96027 5.49201 4.22614 4.22614C5.49201 2.96027 7.2089 2.24911 8.99911 2.24911C10.7893 2.24911 12.5062 2.96027 13.7721 4.22614C15.038 5.49201 15.7491 7.2089 15.7491 8.99911ZM14.5191 16.1091C12.7107 17.5131 10.4352 18.1751 8.15581 17.9603C5.87646 17.7456 3.76463 16.6702 2.25024 14.9532C0.735845 13.2362 -0.0672602 11.0066 0.00441596 8.71824C0.0760921 6.42992 1.01716 4.25492 2.63604 2.63604C4.25492 1.01716 6.42992 0.0760921 8.71824 0.00441596C11.0066 -0.0672602 13.2362 0.735845 14.9532 2.25024C16.6702 3.76463 17.7456 5.87646 17.9603 8.15581C18.1751 10.4352 17.5131 12.7107 16.1091 14.5191L20.2941 18.7041C20.4046 18.8071 20.4933 18.9313 20.5548 19.0693C20.6163 19.2073 20.6493 19.3563 20.652 19.5073C20.6547 19.6584 20.6269 19.8084 20.5703 19.9485C20.5137 20.0886 20.4295 20.2158 20.3227 20.3227C20.2158 20.4295 20.0886 20.5137 19.9485 20.5703C19.8084 20.6269 19.6584 20.6547 19.5073 20.652C19.3563 20.6493 19.2073 20.6163 19.0693 20.5548C18.9313 20.4933 18.8071 20.4046 18.7041 20.2941L14.5191 16.1091Z" fill={active ? "#2BC2E1" : "#BCBCBC"} />
  </svg>
);

const DetailsTabIcon = ({ active }: { active: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <path d="M8 13.7998H16C17.1139 13.7998 18.1821 14.2426 18.9697 15.0303C19.7574 15.8179 20.2002 16.8861 20.2002 18C20.2002 18.5835 19.9682 19.1431 19.5557 19.5557C19.1431 19.9682 18.5835 20.2002 18 20.2002H6C5.41652 20.2002 4.85692 19.9682 4.44434 19.5557C4.03176 19.1431 3.7998 18.5835 3.7998 18L3.80469 17.792C3.85612 16.7538 4.29178 15.7688 5.03027 15.0303C5.81793 14.2426 6.88609 13.7998 8 13.7998ZM12 3.7998C12.8487 3.7998 13.6626 4.13719 14.2627 4.7373C14.8628 5.33742 15.2002 6.15131 15.2002 7C15.2002 7.84869 14.8628 8.66258 14.2627 9.2627C13.6626 9.86281 12.8487 10.2002 12 10.2002C11.1513 10.2002 10.3374 9.86281 9.7373 9.2627C9.13719 8.66258 8.7998 7.84869 8.7998 7C8.7998 6.15131 9.13719 5.33742 9.7373 4.7373C10.3374 4.13719 11.1513 3.7998 12 3.7998Z" stroke={active ? "#2BC2E1" : "#BCBCBC"} strokeWidth="1.6" />
  </svg>
);

const GlobalFilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  border-radius: 10px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SelectWrapper = styled.div<{ isOpen?: boolean }>`
  width: 228px;
  height: 52px;
  border: 1px solid ${({ theme, isOpen }) => (isOpen ? theme.colors.primaryBlue[40] : theme.colors.grayScale[20])};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  position: relative;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primaryBlue[40]};
  }

  ${({ isOpen, theme }) =>
    isOpen &&
    `
    border-color: ${theme.colors.primaryBlue[40]};
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
  `}
`;

const SelectedText = styled.span`
  flex: 1;
  ${({ theme }) => theme.typography.contents.regular24}
  color: ${({ theme }) => theme.colors.grayScale[60]};
`;

const ArrowIcon = styled.div<{ isOpen: boolean }>`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9L12 15L18 9' stroke='%23929292' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

const DropdownList = styled.div`
  position: absolute;
  top: 60px; /* 52px + 8px gap */
  left: 0;
  width: 100%;
  background: white;
  border-radius: 4px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
  padding: 8px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
`;

const DropdownItem = styled.div<{ isActive: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border-radius: 4px;
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme, isActive }) => (isActive ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90])};
  background: ${({ isActive, theme }) => (isActive ? theme.colors.primaryBlue[5] : 'white')};
  cursor: pointer;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryBlue[5]};
    color: ${({ theme }) => theme.colors.primaryBlue[40]};
  }
`;

const SearchInputWrapper = styled.div`
  width: 228px;
  height: 52px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 8px;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  ${({ theme }) => theme.typography.contents.regular24}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  background-color: transparent;
  min-width: 0;
  &::placeholder {
    color: ${({ theme }) => theme.colors.grayScale[60]};
  }
`;

const SearchIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M21 21L16.65 16.65' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const ExcelButton = styled.button`
  height: 50px;
  padding: 0 42px;
  background: ${({ theme }) => theme.colors.green[65]};
  color: white;
  border: none;
  border-radius: 100px;
  ${({ theme }) => theme.typography.button.semiBold20}
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const DownloadIcon = styled.div`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z' fill='white'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const SummaryCard = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 10px;
  padding: 40px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const SummaryTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const ArrowButton = styled.button`
  background: none;
  border: none;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    cursor: not-allowed;
  }
`;

const SummaryTitle = styled.h2`
  color: #17171B;
  text-align: center;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  margin: 0;
`;

const SummaryContent = styled.div`
  width: 571px;
  display: flex;
  flex-direction: column;
`;

const SummaryItem = styled.div<{ variant: 'yellow' | 'white' | 'blue' }>`
  padding: 16px 52px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  background: ${({ theme, variant }) => {
    if (variant === 'yellow') return theme.colors.yellow[5];
    if (variant === 'blue') return theme.colors.primaryBlue[10];
    return theme.colors.grayScale[0];
  }};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemLabelGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemLabel = styled.span<{ size?: 'small' | 'large' }>`
  font-family: Pretendard;
  font-style: normal;

  ${({ size }) =>
    size === 'small'
      ? `
    font-size: 20px;
    font-weight: 400;
    line-height: 150%;
    color: #17171B;
  `
      : `
    font-size: 28px;
    font-weight: 700;
    line-height: 150%;
    color: #676767;
  `}
`;

const ItemValue = styled.span<{ negative?: boolean; highlight?: boolean }>`
  ${({ theme }) => theme.typography.head.bold28}
  font-size: 28px;
  line-height: 42px;
  text-align: right;
  color: ${({ theme, negative, highlight }) => {
    if (highlight) return theme.colors.primaryBlue[40];
    if (negative) return theme.colors.grayScale[90];
    return theme.colors.grayScale[70];
  }};
`;

const ItemDescription = styled.p`
  ${({ theme }) => theme.typography.caption.regular16}
  color: ${({ theme }) => theme.colors.grayScale[60]};
  margin: 0;
`;

const FinalDepositBox = styled.div`
  padding: 16px 52px;
  background: ${({ theme }) => theme.colors.primaryBlue[10]};
  border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40]};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 16px;
`;

const FinalLabel = styled.span`
  ${({ theme }) => theme.typography.head.bold28}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  font-size: 28px;
  line-height: 42px;
`;

const FinalValue = styled.span`
  ${({ theme }) => theme.typography.head.bold28}
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  font-size: 28px;
  line-height: 42px;
  text-align: right;
`;

const NoticeCard = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 10px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  margin-bottom: 120px;
`;

const NoticeTitle = styled.h3`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const NoticeList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

const NoticeItem = styled.li`
  ${({ theme }) => theme.typography.caption.regular16Long}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  display: flex;
  gap: 8px;
  
  &::before {
    content: '•';
  }
`;

const EmptyStateWrapper = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 12px;
  width: 100%;
  min-height: 540px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 56px 48px;
`;

const EmptyTitleBlue = styled.p`
  font-family: Pretendard;
  font-size: 28px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  margin: 0;
  text-align: center;
`;

const EmptyTitleBlack = styled.p`
  font-family: Pretendard;
  font-size: 28px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
  color: ${({ theme }) => theme.colors.grayScale[50]};
  margin: 0;
  text-align: center;
`;

const EmptyClockIcon = styled.svg`
  width: 160px;
  height: 160px;
`;

const ShareBtn = styled.button`
  background: #026478;
  border: none;
  border-radius: 50px;
  padding: 14px 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  cursor: pointer;
  ${({ theme }) => theme.typography.button.bold24}
  color: ${({ theme }) => theme.colors.grayScale[0]};
  white-space: nowrap;

  &:hover {
    border-radius: 50px;
background: var(--PrimaryBlue-80, #026478);
    box-shadow: 0 0 5.8px 0 #357B89;
  }

  &:focus,
  &:active {
    border-radius: 50px;
background: var(--PrimaryBlue-90, #416777);
box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const ShareIcon = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
  aspect-ratio: 1/1;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  width: 644px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 32px 30px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ModalTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  ${({ theme }) => theme.typography.head.semiBold24}
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const ModalDesc = styled.p`
  ${({ theme }) => theme.typography.contents.medium20}
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  margin: 0;
`;

const CloseIcon = styled.div`
  width: 20px;
  height: 20px;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z' fill='%23929292'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const ModalBody = styled.div`
  padding: 0 32px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShareOption = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
`;

const KakaoIconBox = styled.div`
  width: 60px;
  height: 60px;
  background: #f9e000;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KakaoIconImg = styled.img`
  width: 34px;
  height: 34px;
`;

const EmailIconBox = styled.div`
  width: 60px;
  height: 60px;
  background: #EBFCFF;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmailIconLarge = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`;

const ShareOptionText = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const ModalFooter = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[6] || '#f8f8f8'};
  padding: 20px 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LinkBox = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[60]};
  border-right: none;
  border-radius: 10px 0 0 10px;
  padding: 10px 16px;
  width: 402px;
  height: 70px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const LinkLabel = styled.span`
  ${({ theme }) => theme.typography.contents.regular16}
  color: ${({ theme }) => theme.colors.grayScale[50] || '#bcbcbc'};
`;

const LinkUrl = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const CopyButton = styled.button`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[60]};
  border-radius: 0 10px 10px 0;
  padding: 20px 32px;
  height: 70px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  ${({ theme }) => theme.typography.button.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const CopyIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 4H4V16H16V4ZM16 2C17.1 2 18 2.9 18 4V16C18 17.1 17.1 18 16 18H4C2.9 18 2 17.1 2 16V4C2 2.9 2.9 2 4 2H16ZM20 8V20H8V22H20C21.1 22 22 21.1 22 20V8H20Z' fill='%2317171B'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const SharePreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
  padding: 24px;
`;

const SharePreviewContainer = styled.div`
  width: 644px;
  max-width: calc(100vw - 48px);
  background: white;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SharePreviewHeader = styled.div`
  padding: 32px 30px 20px;
  background: var(--GreyScale-6, #F8F8F8);
`;

const SharePreviewTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SharePreviewTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const SharePreviewCloseButton = styled.button`
  background: none;
  border: none;
  width: 20px;
  height: 20px;
  padding: 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z' fill='%23929292'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  cursor: pointer;
`;

const SharePreviewDesc = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const SharePreviewBody = styled.div`
  padding: 20px 30px;
  display: flex;
  justify-content: center;
  background: white;
`;

const SharePreviewImage = styled.img`
  width: 100%;
  max-width: 360px;
  object-fit: contain;
`;

const SharePreviewFooter = styled.div`
  background: #f8f8f8;
  padding: 20px 30px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const SharePreviewBackButton = styled.button`
  height: 70px;
  min-width: 146px;
  border-radius: 10px;
  border: 1px solid #c7c7c7;
  background: #fff;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.button.medium20}
  cursor: pointer;
`;

const SharePreviewSendButton = styled.button`
  height: 70px;
  min-width: 146px;
  border-radius: 10px;
  border: none;
  background: #0a667f;
  color: #fff;
  ${({ theme }) => theme.typography.button.medium20}
  cursor: pointer;
`;

// 결제 상세 내역 컴포넌트들
const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 12px;
`;

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 19px;
  height: 220px;
  background: white;
  border-radius: 12px;
  width: 100%;
`;

const NoResultsText = styled.p`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[50]};
  margin: 0;
`;

const NoResultsIcon = styled.div`
  width: 50px;
  height: 50px;
  background-image: url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 12M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z' stroke='%23bcbcbc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M9 10L9.01 10' stroke='%23bcbcbc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M15 10L15.01 10' stroke='%23bcbcbc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M9 15H15' stroke='%23bcbcbc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const DetailItem = styled.div<{ isFirst: boolean; isLast: boolean }>`
  display: flex;
  gap: 42px;
  padding: 20px 30px;
  border-bottom: ${({ isLast }) => (isLast ? 'none' : `1px solid #eee`)};
  border-top-left-radius: ${({ isFirst }) => (isFirst ? '12px' : '0')};
  border-top-right-radius: ${({ isFirst }) => (isFirst ? '12px' : '0')};
  border-bottom-left-radius: ${({ isLast }) => (isLast ? '12px' : '0')};
  border-bottom-right-radius: ${({ isLast }) => (isLast ? '12px' : '0')};
  align-items: center;
`;

const ProfileImg = styled.img`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  object-fit: cover;
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const ItemHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DonorName = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  font-weight: 600;
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const Badge = styled.span<{ type: 'cms' | 'card' | 'complete' | 'fail' }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  ${({ theme, type }) => {
    switch (type) {
      case 'cms':
        return `background: ${theme.colors.primaryBlue[80]}; color: white;`;
      case 'card':
        return `background: #ebfcff; color: #25b9d8;`;
      case 'complete':
        return `background: #b7f2ff; color: ${theme.colors.grayScale[90]};`;
      case 'fail':
        return `background: white; border: 1px solid #ffc0c0; color: #ff0000;`;
    }
  }}
`;

const DonorInfoGroup = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  ${({ theme }) => theme.typography.caption.regular16}
  color: ${({ theme }) => theme.colors.grayScale[70]};
  line-height: 20px;
`;

const DonorMessage = styled.p`
  ${({ theme }) => theme.typography.contents.regular16}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
  line-height: 1.5;
`;

const AmountSection = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 5px;
`;

const AmountBox = styled.div`
  width: 202px;
  padding: 10px 20px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AmountLabel = styled.span`
  ${({ theme }) => theme.typography.caption.regular16}
  color: inherit;
`;

const AmountValue = styled.span<{ color: 'main' | 'sub' }>`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme, color }) => (color === 'main' ? theme.colors.grayScale[90] : theme.colors.grayScale[70])};
  line-height: 1.5;
`;

const EmailIcon = styled.div`
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 18V6H20V4ZM20 8L12 13L4 8V18H20V8ZM12 11L20 6H4L12 11Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const PhoneIcon = styled.div`
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.06 21 2 10.94 2 1C2 0.45 2.45 0 3 0H6.5C7.05 0 7.5 0.45 7.5 1C7.5 2.25 7.7 3.44 8.06 4.55C8.18 4.9 8.09 5.3 7.82 5.57L6.62 10.79Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const DateIcon = styled.div`
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19 4H18V2H16V4H8V2H6V4H5C3.89 4 2.01 4.9 2.01 6L2 20C2 21.1 2.89 22 4 22H19C20.11 22 21 21.1 21 20V6C21 4.9 20.11 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 400px;
`;

export default PaymentStatusWeb;
